/**
 * Pure list logic behind the private-mode scratchpad — the last few things copied,
 * newest first. Kept free of imports so it can be unit tested on its own (see
 * `npm run test:scratchpad`); the zustand store and the panel live elsewhere.
 */

/** What a scratchpad entry was copied from. Drives the row's label and icon. */
export type ScratchKind = 'settings' | 'prompt' | 'text';

export interface ScratchItem {
  id: string;
  kind: ScratchKind;
  /** The copied text. Re-copying the row puts exactly this back on the clipboard. */
  text: string;
  /**
   * Image for the row: a path under /outputs when the copy came from a generated
   * image, or a `data:` URL once a preview has been generated for a text-only entry.
   */
  thumbnail?: string;
  /** Short provenance line, e.g. the generation settings or the page copied from. */
  source?: string;
  createdAt: number;
}

/** The user asked for the last 10; the cap is the only thing keeping this bounded. */
export const SCRATCHPAD_MAX = 10;

/** FNV-1a. Only needs to separate distinct texts within one millisecond. */
function hashText(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function makeScratchId(text: string, createdAt: number): string {
  return `${createdAt.toString(36)}-${hashText(text)}`;
}

/**
 * Put `item` at the front, dropping any earlier copy of the same text so re-copying
 * something refreshes its position instead of filling the list with duplicates.
 * Blank text is ignored — an empty row would be untraceable and unusable.
 */
export function addScratchItem(
  items: ScratchItem[],
  item: ScratchItem,
  max: number = SCRATCHPAD_MAX
): ScratchItem[] {
  const text = item.text.trim();
  if (!text) return items;
  const previous = items.find((i) => i.text === text);
  const rest = items.filter((i) => i.text !== text);
  // Keep a thumbnail already earned by the earlier copy rather than losing it.
  const thumbnail = item.thumbnail ?? previous?.thumbnail;
  return [{ ...item, text, thumbnail }, ...rest].slice(0, max);
}

export function removeScratchItem(items: ScratchItem[], id: string): ScratchItem[] {
  return items.filter((i) => i.id !== id);
}

export function setScratchThumbnail(
  items: ScratchItem[],
  id: string,
  thumbnail: string
): ScratchItem[] {
  return items.map((i) => (i.id === id ? { ...i, thumbnail } : i));
}

/** Collapses whitespace so multi-line prompts read as one line in a narrow row. */
export function scratchPreview(text: string, max = 140): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
}

export function scratchKindLabel(kind: ScratchKind): string {
  switch (kind) {
    case 'settings':
      return 'Settings';
    case 'prompt':
      return 'Prompt';
    default:
      return 'Text';
  }
}

/** Compact generation settings for the row subtitle, e.g. "1024×1024 · 9 steps · cfg 1 · seed 777". */
export function scratchParamsSummary(params: {
  width?: number;
  height?: number;
  steps?: number;
  cfg?: number;
  seed?: number | null;
}): string {
  const parts: string[] = [];
  if (params.width && params.height) parts.push(`${params.width}×${params.height}`);
  if (params.steps !== undefined) parts.push(`${params.steps} steps`);
  if (params.cfg !== undefined) parts.push(`cfg ${params.cfg}`);
  if (params.seed !== undefined && params.seed !== null) parts.push(`seed ${params.seed}`);
  return parts.join(' · ');
}
